import {
  ErrorMessageMapperProvider,
  ModalProvider,
  ThemeProvider,
  ToastContainer,
} from '@modusign/design-system';
import GlobalStyles from '@modusign/design-system/GlobalStyles';
import { SigningStateProvider } from '@modusign/react-signing';
import { SigningState } from '@modusign/react-signing/types';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useEffect } from 'react';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider } from 'react-redux';
import { Router } from 'react-router';

import AppGlobalStyles from '@app/components/AppGlobalStyles';
import { RoutePathProvider } from '@app/components/Route/RoutePathProvider';
import GOOGLE_CLIENT_ID from '@app/constants/googleClientId';
import { GNBFoldHandleProvider } from '@app/containers/GlobalNavigationBar/GNBFoldHandleContext';
import { MainPageHeaderProvider } from '@app/hooks/useMainPageHeaderState';
import { PDFProvider } from '@app/hooks/usePDF';
import authService from '@app/libs/auth/authService';
import documentAuthService from '@app/libs/auth/documentAuthService';
import coalesceGlobalErrorMessage from '@app/libs/coalesceGlobalErrorMessage';
import { setDatadogContextProperty, setDatadogUser } from '@app/libs/datadog';
import { logger } from '@app/libs/logger';
import workspaceService from '@app/libs/workspaceService';
import { queryClient } from '@app/queryClient';
import Routes from '@app/routes';
import store from '@app/store';
import { loadAccount } from '@app/store/account/actions';
import { createReduxHistory } from '@app/store/history';
import { resetStore } from '@app/store/rootReducer';
import theme from '@app/theme';

const reduxHistory = createReduxHistory(store);

function Root() {
  useEffect(
    () =>
      workspaceService.subscribe(() => {
        if (workspaceService.hasWorkspaceId()) {
          logger.info(`workspace id has changed to ${workspaceService.getWorkspaceId()}`);
          setDatadogContextProperty('workspaceId', workspaceService.getWorkspaceId());
        } else {
          logger.info(`workspace id has removed`);
          setDatadogContextProperty('workspaceId', null);
        }

        queryClient.removeQueries();
        if (
          authService.hasAccessToken() &&
          // TODO(@highcastlee): refreshToken 정상 도입 후 refreshToken 만료만 확인하도록 수정합니다.
          (authService.hasRefreshToken()
            ? !authService.isRefreshTokenExpired()
            : !authService.isAccessTokenExpired()) &&
          !documentAuthService.isLinkUser()
        ) {
          store.dispatch(loadAccount());
        }
      }),
    []
  );

  useEffect(
    () =>
      authService.subscribe(() => {
        logger.info('token has changed');

        setDatadogUser();

        /**
         * @FIXME
         * react-query QueryKey가 유니크함이 보장되면 (v4 + eslint)
         * workspaceService.subscribe의 쿼리클라이언트 초기화를 없애고 여기서만 쿼리클라이언트 초기화를 진행해주세요.
         * 쿼리키가 유니크하다면 workspaceId가 변할 때 초기화하지 않아도 경합이 발생하지 않는 것이 보장됩니다.
         *
         * jwt 토큰의 변화를 감지했을때, store를 초기화합니다.
         */
        const { pathname } = window.location;
        if (
          /**
           * 서명요청 플로우일 때, store를 초기화하면 documentId가 유실되어 제외합니다.
           * @see https://modusign.slack.com/archives/CBT88M56C/p1697163560667899?thread_ts=1697161835.212579&cid=CBT88M56C
           */
          !pathname.startsWith('/embedded-participant') &&
          /** 카카오 자동로그인 */
          !pathname.startsWith('/authentication/kakao-callback') &&
          /** '/participant'에서 링크 유저가 로그인 후 문서 상태에 따라 페이지를 전환합니다. */
          !pathname.startsWith('/participant') &&
          !/^\/link\/[^/]+\/authentication/.test(pathname) &&
          /** '/signing/progress' 페이지에서 "사인/도장 모달"을 통해 로그인하고, 이미 가입된 계정에서 만든 사인도장을 사용할 수 있습니다. */
          !pathname.startsWith('/signing/progress')
        ) {
          workspaceService.removeWorkspaceId();
          store.dispatch(resetStore());
        }
      }),
    []
  );

  useEffect(() => {
    // TODO(@highcastlee): refreshToken 정상 도입 후 refreshToken 만료만 확인하도록 수정합니다.
    if (
      authService.hasRefreshToken()
        ? authService.isRefreshTokenExpired()
        : authService.isAccessTokenExpired()
    ) {
      // FIXME: authService가 만료된 토큰을 지우게 하기
      // 서비스 초기화시 또는 그 이후 토큰이 만료되었을 때 authService가 알아서 토큰을 지우게 하기.
      // 현재 authService instance가 만들어진 후 bootstrap에서 따로 storage를 주입합니다.
      // 따라서 AuthService contructor에서는 storage가 설정이 안되어있기 때문에 토큰을 가져올 수 없습니다.
      // AuthService에 storage를 주입하는 방법을 constructor injection으로 바꾸면 constructor에서 만료된 토큰을 제거할 수 있습니다.
      authService.removeTokens();
    } else if (
      authService.hasAccessToken() &&
      !documentAuthService.isLinkUser() &&
      window.location.pathname !== '/signout'
    ) {
      store.dispatch(loadAccount());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ErrorMessageMapperProvider value={(message) => coalesceGlobalErrorMessage(message)}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <GlobalStyles />
          <AppGlobalStyles />
          <ToastContainer />
          <RoutePathProvider>
            <Router history={reduxHistory}>
              <Provider store={store}>
                <SigningStateProvider value={initialSigningState}>
                  <GNBFoldHandleProvider>
                    <PDFProvider>
                      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                        <ModalProvider>
                          <MainPageHeaderProvider>
                            <Routes />
                          </MainPageHeaderProvider>
                        </ModalProvider>
                      </GoogleOAuthProvider>
                    </PDFProvider>
                  </GNBFoldHandleProvider>
                </SigningStateProvider>
              </Provider>
            </Router>
          </RoutePathProvider>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorMessageMapperProvider>
  );
}

export default Root;

const initialSigningState: SigningState = {
  fields: [],
  attachments: [],
  document: {
    pdfFile: {},
    pdfPassword: null,
    title: '',
  },
  participants: [],
  verifications: [],
  ui: {
    setup: {
      insertingField: null,
      lastInsertedField: {
        REQUESTER: {
          CHECKBOX: null,
          CHECKBOX_GROUP: null,
          IMAGE: null,
          SIGNATURE: null,
          TEXT: null,
          DROPDOWN: null,
          AUTOFILL_GROUP: null,
          NAME: null,
          COMPANY_NAME: null,
          ADDRESS: null,
          DATE: null,
          SIGNING_DATE: null,
        },
        SIGNER: {
          CHECKBOX: null,
          CHECKBOX_GROUP: null,
          IMAGE: null,
          SIGNATURE: null,
          TEXT: null,
          DROPDOWN: null,
          AUTOFILL_GROUP: null,
          NAME: null,
          COMPANY_NAME: null,
          ADDRESS: null,
          DATE: null,
          SIGNING_DATE: null,
        },
      },
      isInOrder: null,
    },
    signing: {
      currentSigner: null,
      initialSignatureField: null,
    },
  },
};
